/**
 * WHAT THE PANEL REMEMBERS BETWEEN VISITS.
 *
 * Closing a tab is not the same as finishing a conversation, and reloading a page is not a decision
 * to abandon what you had typed. Three things survive: which thread was open, the half-written
 * message in the box, and — already, elsewhere — the panel's width.
 *
 * Local, never sent. A draft is text the person has NOT chosen to send; putting it on the wire to
 * make it durable would publish exactly the thing they were still deciding about. The archive gets
 * messages when they are sent, and not before.
 *
 * Keyed by PLATFORM, because the same browser runs more than one of these — the AI console and the
 * gateway app each have their own panel, and reopening one into the other's thread would be a
 * surprise rather than a convenience.
 */
const KEY = 'vitesse.ai.session';
/**
 * How many drafts to keep.
 *
 * Bounded because this grows without one: a person who starts twenty threads and abandons a
 * sentence in each would otherwise carry all twenty forever. Oldest-written go first, which is the
 * order in which a half-typed message stops being worth restoring.
 */
const MAX_DRAFTS = 20;
/** How long a draft is worth restoring. Older than this it is not resumption, it is archaeology. */
const DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000;
/** A draft longer than this is not a message someone was typing; it is a paste that should not sit
 *  in storage indefinitely. Kept, but truncated. */
const MAX_DRAFT_CHARS = 8000;
/** Read the whole store. Anything unparseable is treated as empty — a corrupted entry must not be
 *  able to break the panel's boot. */
function read() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    }
    catch {
        return {}; // private mode, storage disabled, or corrupt
    }
}
function write(store) {
    try {
        localStorage.setItem(KEY, JSON.stringify(store));
    }
    catch {
        // Full, or disabled. Losing a remembered draft is not worth failing a render for.
    }
}
const memOf = (store, platform) => (store[platform] ??= {});
/** Remember which thread is open. Passing '' forgets it — that is what closing a thread means. */
export function rememberConversation(platform, conversationId) {
    const store = read();
    const mem = memOf(store, platform);
    if (conversationId)
        mem.conversationId = conversationId;
    else
        delete mem.conversationId;
    write(store);
}
/** The thread to reopen, or '' when there is none worth reopening. */
export function lastConversation(platform) {
    return read()[platform]?.conversationId ?? '';
}
/**
 * Keep what is in the box.
 *
 * Empty CLEARS it, rather than storing an empty string — a person who selected all and deleted has
 * decided, and restoring an empty draft over a fresh box is a no-op that still costs a write.
 */
/*
 * THE THREAD A DRAFT BELONGS TO — and the one it belongs to before it exists.
 *
 * A draft is keyed by conversation, and a NEW chat has no conversation until its first message is
 * sent. So the one composer people type the most into — the empty one — persisted nothing: type a
 * page, step back to the list, come back, and it was gone. The guard that produced that was
 * `if (!conversationId) return`, which is correct about the key and wrong about the case.
 *
 * A reserved key rather than a second store: a new chat is a thread whose id is not settled yet,
 * and it expires, counts against the cap and is cleared on send exactly like any other draft. The
 * colon cannot collide with a real id, which is a uuid.
 */
export const NEW_CHAT_DRAFT = 'new:';
export function saveDraft(platform, conversationId, text, mentions = []) {
    if (!conversationId)
        return;
    const store = read();
    const mem = memOf(store, platform);
    const drafts = (mem.drafts ??= {});
    // Delete THEN re-add, so a re-saved draft moves to the end of insertion order. That order is what
    // the cap uses; sorting by timestamp instead made the survivors arbitrary whenever two writes
    // landed in the same millisecond, which is most of them while somebody is typing.
    delete drafts[conversationId];
    if (text.trim())
        drafts[conversationId] = {
            text: text.slice(0, MAX_DRAFT_CHARS),
            at: Date.now(),
            ...(mentions.length ? { mentions: [...mentions] } : {}),
        };
    mem.drafts = prune(drafts);
    write(store);
}
/** What was in the box, or '' — expired and unknown threads both read as nothing. */
export function readDraft(platform, conversationId) {
    const d = read()[platform]?.drafts?.[conversationId];
    if (!d || typeof d.text !== 'string')
        return '';
    return Date.now() - (d.at ?? 0) > DRAFT_TTL_MS ? '' : d.text;
}
/** The mentions saved with a draft, unchecked — see `Draft.mentions`. Empty when there are none or
 *  the draft has expired. */
export function readDraftMentions(platform, conversationId) {
    const d = read()[platform]?.drafts?.[conversationId];
    if (!d || Date.now() - (d.at ?? 0) > DRAFT_TTL_MS || !Array.isArray(d.mentions))
        return [];
    return d.mentions;
}
/** Drop it, once it has become a message. */
export function clearDraft(platform, conversationId) {
    saveDraft(platform, conversationId, '');
}
/**
 * The most recently written MAX_DRAFTS, expired ones dropped.
 *
 * Recency is INSERTION ORDER, not the clock: several drafts saved in the same millisecond are
 * indistinguishable by timestamp, and a cap that keeps an arbitrary subset of them is a cap that
 * throws away what you were just typing. The clock is still what decides expiry, where the
 * granularity is days and cannot tie.
 *
 * Pruning on write keeps the cap honest without a sweep.
 */
function prune(drafts) {
    const now = Date.now();
    const live = Object.entries(drafts).filter(([, d]) => d && now - (d.at ?? 0) <= DRAFT_TTL_MS);
    return Object.fromEntries(live.slice(-MAX_DRAFTS));
}
//# sourceMappingURL=session-memory.js.map